import firebase from 'firebase'
import { groups, groupMembers, groupDomains } from '../../firebase/collections'

/**
 * Load all workspace groups the current user owns or has joined as an active member.
 * Optionally includes bookmarkCount per group.
 *
 * @param {Object} options
 * @param {boolean} [options.includeBookmarkCounts=false]
 * @returns {Promise<Array>} Sorted list of group objects with token, isOwner/isMember
 */
export async function getUserWorkspaceGroups(options = {}) {
  const { includeBookmarkCounts = false } = options
  const currentUser = firebase.auth().currentUser

  if (!currentUser) {
    return []
  }

  const ownerGroups = await groups
    .where('ownerId', '==', currentUser.uid)
    .get()
    .then(snapshot =>
      snapshot.docs.map(doc => ({
        ...doc.data(),
        token: doc.id,
        isOwner: true
      }))
    )

  const allGroupsSnapshot = await groups
    .where('ownerId', '!=', currentUser.uid)
    .get()
  const memberGroups = []

  await Promise.all([
    ...(includeBookmarkCounts
      ? ownerGroups.map(async group => {
          const bookmarkSnapshot = await groupDomains
            .doc(group.token)
            .collection('categories')
            .get()
          group.bookmarkCount = bookmarkSnapshot.size
        })
      : []),
    ...allGroupsSnapshot.docs.map(async groupDoc => {
      const memberDoc = await groupMembers
        .doc(groupDoc.id)
        .collection('members')
        .doc(currentUser.uid)
        .get()

      if (memberDoc.exists && memberDoc.data().status === 'active') {
        const group = {
          ...groupDoc.data(),
          token: groupDoc.id,
          isMember: true,
          isOwner: groupDoc.data().ownerId === currentUser.uid
        }

        if (includeBookmarkCounts) {
          const bookmarkSnapshot = await groupDomains
            .doc(groupDoc.id)
            .collection('categories')
            .get()
          group.bookmarkCount = bookmarkSnapshot.size
        }

        memberGroups.push(group)
      }
    })
  ])

  const allGroups = [...ownerGroups, ...memberGroups]
  return allGroups.sort((a, b) =>
    (a.groupName || a.name || '').localeCompare(b.groupName || b.name || '')
  )
}

/**
 * Flatten bookmarks from a single group's categories collection,
 * tagging each with workspace metadata.
 *
 * @param {Object} group - Group with token/id and name fields
 * @returns {Promise<Array>}
 */
async function fetchGroupBookmarks(group) {
  const groupToken = group.token || group.id
  const groupName = group.groupName || group.name || 'Workspace'
  const snapshot = await groupDomains
    .doc(groupToken)
    .collection('categories')
    .get()

  return snapshot.docs.map(doc => {
    const data = doc.data()
    return {
      ...data,
      doc_id: data.doc_id || doc.id,
      categoryTitle: data.categoryTitle || data.category || 'Uncategorised',
      groupToken,
      groupName,
      groupSlug: group.slug || null,
      groupSettings: group.settings || null,
      isOwner: !!group.isOwner,
      isMember: !!group.isMember,
      isPublic: group.settings?.isPrivate === false || group.isPublic === true
    }
  })
}

/**
 * Load all bookmarks across every workspace the user belongs to.
 *
 * @param {Object} options
 * @param {Array} [options.groups] - Pre-loaded groups (skips group fetch if provided)
 * @param {boolean} [options.includeBookmarkCounts=false] - When fetching groups, include counts
 * @returns {Promise<{ groups: Array, bookmarks: Array }>}
 */
export async function fetchAllWorkspaceBookmarks(options = {}) {
  const { groups: preloadedGroups, includeBookmarkCounts = false } = options

  const workspaceGroups =
    preloadedGroups || (await getUserWorkspaceGroups({ includeBookmarkCounts }))

  if (!workspaceGroups.length) {
    return { groups: [], bookmarks: [] }
  }

  const bookmarkArrays = await Promise.all(
    workspaceGroups.map(group => fetchGroupBookmarks(group))
  )

  const bookmarks = bookmarkArrays.flat()

  // If counts were not pre-fetched, derive from loaded bookmarks
  if (!includeBookmarkCounts || !preloadedGroups) {
    const countsByToken = {}
    bookmarks.forEach(b => {
      countsByToken[b.groupToken] = (countsByToken[b.groupToken] || 0) + 1
    })
    workspaceGroups.forEach(group => {
      if (group.bookmarkCount === undefined) {
        group.bookmarkCount = countsByToken[group.token] || 0
      }
    })
  }

  return { groups: workspaceGroups, bookmarks }
}

export default {
  getUserWorkspaceGroups,
  fetchAllWorkspaceBookmarks
}
